<template>
  <div class="container-fluid p-4">

    <!-- Encabezado -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h4 class="mb-0"><i class="bi bi-building-check me-2"></i>Estado de Cuenta de Proveedores</h4>
        <p class="text-muted small mb-0">Compras a crédito — historial y pagos pendientes</p>
      </div>
      <button class="btn btn-outline-secondary btn-sm" @click="cargar" :disabled="cargando">
        <i class="bi bi-arrow-clockwise"></i> Actualizar
      </button>
    </div>

    <!-- Filtros -->
    <div class="card mb-4 shadow-sm">
      <div class="card-body py-2">
        <div class="row gy-2 align-items-end">
          <div class="col-md-6">
            <label class="form-label small mb-1">Buscar proveedor</label>
            <div class="input-group input-group-sm">
              <input
                type="text"
                class="form-control form-control-sm"
                placeholder="RUC o nombre"
                v-model="filtros.busqueda"
                @keyup.enter="cargar"
              />
              <button class="btn btn-outline-primary btn-sm" @click="cargar">
                <i class="bi bi-search"></i>
              </button>
            </div>
          </div>
          <div class="col-md-4">
            <label class="form-label small mb-1">Estado crédito</label>
            <select class="form-select form-select-sm" v-model="filtros.estadoCredito" @change="cargar">
              <option value="">Todos</option>
              <option value="PENDIENTE">Pendiente</option>
              <option value="PARCIAL">Parcialmente pagado</option>
              <option value="VENCIDO">Vencido</option>
              <option value="PAGADO">Pagado</option>
            </select>
          </div>
          <div class="col-md-2 d-flex align-items-end">
            <button class="btn btn-outline-secondary btn-sm w-100" @click="limpiarFiltros">Limpiar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- KPIs resumen -->
    <div v-if="!cargando && compras.length > 0" class="row gy-2 mb-4">
      <div class="col-md-4">
        <div class="card border-primary text-center py-2">
          <div class="fw-bold fs-5 text-primary">{{ compras.length }}</div>
          <div class="small text-muted">Compras a crédito</div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card border-warning text-center py-2">
          <div class="fw-bold fs-5 text-warning">$ {{ totalPendiente.toFixed(2) }}</div>
          <div class="small text-muted">Total por pagar</div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card border-danger text-center py-2">
          <div class="fw-bold fs-5 text-danger">{{ comprasVencidas }}</div>
          <div class="small text-muted">Compras vencidas</div>
        </div>
      </div>
    </div>

    <!-- Tabla -->
    <div class="card shadow-sm">
      <div class="card-body p-0">
        <div v-if="cargando" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
          <p class="mt-2 text-muted">Cargando estado de cuenta...</p>
        </div>
        <div v-else-if="compras.length === 0" class="text-center py-5 text-muted">
          <i class="bi bi-inbox fs-2 d-block mb-2"></i>
          No se encontraron compras a crédito con los filtros aplicados
        </div>
        <div v-else class="table-responsive">
          <table class="table table-sm table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th>N.° Comprobante</th>
                <th>Emisión</th>
                <th>Proveedor</th>
                <th class="text-end">Total factura</th>
                <th class="text-end">Pagado</th>
                <th class="text-end">Saldo pendiente</th>
                <th>Vencimiento</th>
                <th>Estado</th>
                <th class="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in comprasPaginadas" :key="c.id" :class="{ 'table-danger': c.vencida == 1 }">
                <td class="small">{{ c.numero_factura || '-' }}</td>
                <td class="small">{{ fmtFecha(c.fecha_emision) }}</td>
                <td class="small">
                  <div>{{ c.proveedor_nombre }}</div>
                  <small class="text-muted">{{ c.proveedor_ruc }}</small>
                </td>
                <td class="text-end small">$ {{ fmtMonto(c.total) }}</td>
                <td class="text-end small text-success">$ {{ fmtMonto(c.monto_abonado) }}</td>
                <td class="text-end small fw-bold" :class="c.vencida == 1 ? 'text-danger' : 'text-warning'">
                  $ {{ fmtMonto(c.saldo_pendiente) }}
                </td>
                <td class="small" :class="{ 'text-danger fw-semibold': c.vencida == 1 }">
                  {{ fmtFecha(c.fecha_vencimiento_credito) || calcularVencimiento(c) }}
                </td>
                <td>
                  <span :class="badgeEstado(c.estado_credito)">
                    {{ etiquetaEstado(c.estado_credito) }}
                  </span>
                </td>
                <td class="text-center">
                  <div class="btn-group btn-group-sm">
                    <router-link
                      :to="`/compras/ordenes/${c.id}`"
                      class="btn btn-outline-secondary btn-sm"
                      title="Ver compra"
                    >
                      <i class="bi bi-eye"></i>
                    </router-link>
                    <button
                      v-if="parseFloat(c.saldo_pendiente) > 0"
                      class="btn btn-success btn-sm"
                      title="Registrar pago"
                      @click="abrirPago(c)"
                    >
                      <i class="bi bi-cash-coin"></i> Pagar
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Paginación -->
        <div v-if="totalPaginas > 1" class="d-flex justify-content-between align-items-center px-3 py-2 border-top small">
          <span class="text-muted">Mostrando {{ paginaDesde }}–{{ paginaHasta }} de {{ compras.length }}</span>
          <nav aria-label="Paginación">
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" :class="{ disabled: pagina === 1 }">
                <button class="page-link" @click="pagina--" :disabled="pagina === 1">‹</button>
              </li>
              <li v-for="p in paginasVisibles" :key="p" class="page-item" :class="{ active: pagina === p, disabled: p === '...' }">
                <button class="page-link" @click="p !== '...' && (pagina = p)">{{ p }}</button>
              </li>
              <li class="page-item" :class="{ disabled: pagina === totalPaginas }">
                <button class="page-link" @click="pagina++" :disabled="pagina === totalPaginas">›</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Alerta -->
    <div v-if="alerta.mensaje" :class="`alert alert-${alerta.tipo} mt-3 alert-dismissible`" role="alert">
      {{ alerta.mensaje }}
      <button type="button" class="btn-close" @click="alerta.mensaje = ''"></button>
    </div>

    <!-- Modal de pago -->
    <div v-if="pago.visible" class="modal-fondo" @click.self="cerrarPago">
      <div class="modal-caja">
        <div class="modal-cabecera">
          <h5 class="mb-0"><i class="bi bi-cash-coin me-2 text-success"></i>Registrar Pago</h5>
          <button class="btn-cerrar" @click="cerrarPago">&times;</button>
        </div>

        <div class="modal-cuerpo">
          <!-- Detalle de la compra -->
          <div class="detalle-factura mb-3">
            <div class="detalle-fila">
              <span class="detalle-etiqueta">Proveedor</span>
              <span>{{ pago.compra.proveedor_nombre }}</span>
            </div>
            <div class="detalle-fila">
              <span class="detalle-etiqueta">Comprobante</span>
              <span>{{ pago.compra.numero_factura }}</span>
            </div>
            <div class="detalle-fila">
              <span class="detalle-etiqueta">Total factura</span>
              <span>$ {{ fmtMonto(pago.compra.total) }}</span>
            </div>
            <div class="detalle-fila">
              <span class="detalle-etiqueta">Ya pagado</span>
              <span class="text-success">$ {{ fmtMonto(pago.compra.monto_abonado) }}</span>
            </div>
            <div class="detalle-fila fw-bold">
              <span class="detalle-etiqueta">Saldo pendiente</span>
              <span class="text-danger">$ {{ fmtMonto(pago.compra.saldo_pendiente) }}</span>
            </div>
          </div>

          <!-- Monto -->
          <div class="mb-3">
            <label class="form-label small fw-semibold">Monto a pagar <span class="text-danger">*</span></label>
            <div class="input-group">
              <span class="input-group-text">$</span>
              <input
                type="number"
                class="form-control"
                step="0.01"
                min="0.01"
                :max="parseFloat(pago.compra.saldo_pendiente || 0)"
                v-model="pago.monto_abono"
                @focus="$event.target.select()"
              />
            </div>
            <div class="form-text">Máximo: $ {{ fmtMonto(pago.compra.saldo_pendiente) }}</div>
          </div>

          <!-- Observación -->
          <div class="mb-3">
            <label class="form-label small fw-semibold">
              Observación <span class="text-muted">(opcional)</span>
            </label>
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder="Ej: transferencia #123, pago en efectivo..."
              v-model="pago.observacion"
              maxlength="200"
            />
          </div>

          <!-- Fuente de pago -->
          <div class="mb-3">
            <label class="form-label small fw-semibold">Pagar desde <span class="text-danger">*</span></label>
            <div class="d-flex gap-2 mb-2">
              <button
                type="button"
                :class="['btn btn-sm flex-fill', pago.fuente_pago === 'caja' ? 'btn-primary' : 'btn-outline-secondary']"
                @click="pago.fuente_pago = 'caja'; pago.caja_id = cajas[0]?.id ?? null"
              >
                <i class="bi bi-cash-coin me-1"></i> Caja
              </button>
              <button
                type="button"
                :class="['btn btn-sm flex-fill', pago.fuente_pago === 'banco' ? 'btn-primary' : 'btn-outline-secondary']"
                @click="pago.fuente_pago = 'banco'; pago.banco_id = bancos[0]?.id ?? null"
              >
                <i class="bi bi-bank me-1"></i> Banco
              </button>
            </div>
            <!-- Selector de caja -->
            <template v-if="pago.fuente_pago === 'caja'">
              <select v-model="pago.caja_id" class="form-select form-select-sm">
                <option :value="null">-- Seleccionar caja --</option>
                <option v-for="c in cajas" :key="c.id" :value="c.id">{{ c.nombre_caja }}</option>
              </select>
              <div v-if="cajas.length === 0" class="form-text text-warning">No hay cajas disponibles</div>
            </template>
            <!-- Selector de banco -->
            <template v-if="pago.fuente_pago === 'banco'">
              <select v-model="pago.banco_id" class="form-select form-select-sm">
                <option :value="null">-- Seleccionar banco --</option>
                <option v-for="b in bancos" :key="b.id" :value="b.id">{{ b.nombre_banco }} — {{ b.numero_cuenta }}</option>
              </select>
              <div v-if="bancos.length === 0" class="form-text text-warning">No hay cuentas bancarias configuradas</div>
            </template>
          </div>

          <div v-if="pago.error" class="alert alert-danger py-2 small">{{ pago.error }}</div>
        </div>

        <div class="modal-pie">
          <button class="btn btn-outline-secondary btn-sm" @click="cerrarPago" :disabled="pago.cargando">
            Cancelar
          </button>
          <button
            class="btn btn-success btn-sm"
            @click="confirmarPago"
            :disabled="pago.cargando || !pago.monto_abono || parseFloat(pago.monto_abono) <= 0"
          >
            <span v-if="pago.cargando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-check-circle me-1"></i>
            Confirmar pago
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'
import notificaciones from '@/services/notificationService'

const pagoVacio = () => ({
  visible:      false,
  compra:       null,
  monto_abono:  '',
  observacion:  '',
  fuente_pago:  'caja',  // 'caja' | 'banco'
  caja_id:      null,
  banco_id:     null,
  cargando:     false,
  error:        '',
})

export default {
  name: 'ClientEstadoCuentaProveedor',

  data() {
    return {
      cargando: false,
      compras:  [],
      bancos:   [],
      cajas:    [],
      pagina: 1,
      porPagina: 25,
      filtros: {
        busqueda:      '',
        estadoCredito: '',
      },
      alerta: { mensaje: '', tipo: 'success' },
      pago:   pagoVacio(),
    }
  },

  computed: {
    totalPendiente() {
      return this.compras.reduce((s, c) => s + parseFloat(c.saldo_pendiente || 0), 0)
    },
    comprasVencidas() {
      return this.compras.filter(c => c.vencida == 1).length
    },
    totalPaginas() {
      return Math.ceil(this.compras.length / this.porPagina) || 1
    },
    paginaDesde() {
      return (this.pagina - 1) * this.porPagina + 1
    },
    paginaHasta() {
      return Math.min(this.pagina * this.porPagina, this.compras.length)
    },
    comprasPaginadas() {
      const desde = (this.pagina - 1) * this.porPagina
      return this.compras.slice(desde, desde + this.porPagina)
    },
    paginasVisibles() {
      const total = this.totalPaginas
      const actual = this.pagina
      const paginas = []
      if (total <= 7) {
        for (let i = 1; i <= total; i++) paginas.push(i)
      } else {
        paginas.push(1)
        if (actual > 3) paginas.push('...')
        for (let i = Math.max(2, actual - 1); i <= Math.min(total - 1, actual + 1); i++) paginas.push(i)
        if (actual < total - 2) paginas.push('...')
        paginas.push(total)
      }
      return paginas
    },
  },

  mounted() {
    this.cargar()
    this.cargarFuentesPago()
  },

  methods: {
    async cargar() {
      this.cargando = true
      this.pagina   = 1
      this.alerta   = { mensaje: '', tipo: 'success' }
      try {
        const params = { limit: 500 }
        if (this.filtros.busqueda)      params.q              = this.filtros.busqueda
        if (this.filtros.estadoCredito) params.estado_credito = this.filtros.estadoCredito

        const { data } = await api.get('/compras/estado-cuenta', { params })
        this.compras = data.data || []
      } catch (e) {
        notificaciones.error('No se pudo cargar el estado de cuenta de proveedores.')
      } finally {
        this.cargando = false
      }
    },

    limpiarFiltros() {
      this.filtros = { busqueda: '', estadoCredito: '' }
      this.cargar()
    },

    // ── Carga bancos y cajas disponibles ──────────────────────────────
    async cargarFuentesPago() {
      try {
        const [resBancos, resCajas] = await Promise.all([
          api.get('/bancos'),
          api.get('/cajas'),
        ])
        this.bancos = resBancos.data?.bancos || resBancos.data || []
        this.cajas  = resCajas.data?.cajas  || resCajas.data  || []
      } catch {
        // No bloquear la página si falla
      }
    },

    // ── Modal de pago ──────────────────────────────────────────────────────
    abrirPago(c) {
      this.pago = {
        visible:     true,
        compra:      c,
        monto_abono: parseFloat(c.saldo_pendiente || 0).toFixed(2),
        observacion: '',
        fuente_pago: 'caja',
        caja_id:     this.cajas[0]?.id ?? null,
        banco_id:    this.bancos[0]?.id ?? null,
        cargando:    false,
        error:       '',
      }
    },

    cerrarPago() {
      this.pago = pagoVacio()
    },

    async confirmarPago() {
      const { compra, monto_abono, observacion, fuente_pago, caja_id, banco_id } = this.pago
      if (!compra?.id) return

      const monto = parseFloat(monto_abono)
      if (!monto || monto <= 0) {
        this.pago.error = 'El monto debe ser mayor a cero'
        return
      }
      const saldoMax = parseFloat(compra.saldo_pendiente || 0)
      if (monto > saldoMax + 0.005) {
        this.pago.error = `El monto no puede superar el saldo pendiente ($ ${saldoMax.toFixed(2)})`
        return
      }
      if (fuente_pago === 'caja' && !caja_id) {
        this.pago.error = 'Selecciona una caja para registrar el pago'
        return
      }
      if (fuente_pago === 'banco' && !banco_id) {
        this.pago.error = 'Selecciona un banco para registrar el pago'
        return
      }

      this.pago.cargando = true
      this.pago.error    = ''
      try {
        const payload = {
          monto_abono: monto,
          observacion: observacion.trim(),
          fuente_pago,
        }
        if (fuente_pago === 'caja')  payload.caja_id  = caja_id
        if (fuente_pago === 'banco') payload.banco_id = banco_id

        await api.post(`/compras/${compra.id}/abonar`, payload)
        this.cerrarPago()
        this.alerta = {
          mensaje: `Pago de $ ${monto.toFixed(2)} registrado correctamente.`,
          tipo:    'success',
        }
        await this.cargar()
      } catch (e) {
        const resp = e?.response?.data
        this.pago.error = resp?.error || 'No se pudo registrar el pago'
      } finally {
        this.pago.cargando = false
      }
    },

    // ── Utilidades ─────────────────────────────────────────────────────────
    fmtFecha(f) {
      return f ? f.slice(0, 10) : '-'
    },

    fmtMonto(v) {
      return parseFloat(v || 0).toFixed(2)
    },

    calcularVencimiento(c) {
      if (c.fecha_vencimiento_credito) return c.fecha_vencimiento_credito.slice(0, 10)
      if (!c.fecha_emision || !c.dias_plazo_credito) return '-'
      const d = new Date(c.fecha_emision)
      d.setDate(d.getDate() + parseInt(c.dias_plazo_credito || 0))
      return d.toISOString().slice(0, 10)
    },

    badgeEstado(estado) {
      const mapa = {
        pendiente: 'badge bg-warning text-dark',
        parcial:   'badge bg-info text-dark',
        pagado:    'badge bg-success',
        vencido:   'badge bg-danger',
      }
      return mapa[(estado || 'pendiente').toLowerCase()] || 'badge bg-secondary'
    },

    etiquetaEstado(estado) {
      const mapa = {
        pendiente: 'Pendiente',
        parcial:   'Parcial',
        pagado:    'Pagado',
        vencido:   'Vencido',
      }
      return mapa[(estado || 'pendiente').toLowerCase()] || 'Pendiente'
    },
  },
}
</script>

<style scoped>
/* ── Modal ─────────────────────────────────────────────────────────── */
.modal-fondo {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 16px;
}

.modal-caja {
  background: var(--secondary-bg-main);
  border-radius: 14px;
  width: 100%;
  max-width: 480px;
  box-shadow: var(--shadow-md);
  overflow: hidden;
  color: var(--text-primary);
}

.dark-theme .modal-caja {
  background: var(--secondary-bg-main);
  color: var(--text-primary);
}

.modal-cabecera {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--bg-main);
  border-bottom: 1px solid var(--border-main);
}

.btn-cerrar {
  background: none;
  border: none;
  font-size: 1.4rem;
  line-height: 1;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0 4px;
}
.btn-cerrar:hover { color: var(--text-primary); }

.modal-cuerpo {
  padding: 20px;
}

.modal-pie {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  background: var(--bg-main);
  border-top: 1px solid var(--border-main);
}

/* ── Detalle compra en modal ──────────────────────────────────────── */
.detalle-factura {
  background: var(--bg-main);
  border-radius: 10px;
  padding: 12px 14px;
  border-left: 4px solid var(--accent-success);
}

.detalle-fila {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 0.88rem;
  border-bottom: 1px solid var(--border-main);
}
.detalle-fila:last-child { border-bottom: none; }

.detalle-etiqueta { color: var(--text-secondary); }
</style>
